import React, { useState } from 'react';
import Tabs from '~/components/molecule/Tabs';
import TabBody from '~/components/molecule/Tabs/components/TabBody';
import { Heading3, Body } from '~/components/atom/Typography';
import TEST_ID from './index.testid';
import { navigate } from '@gatsbyjs/reach-router';

import { useGetFlowsV2StatisticsQuery } from '~/graphql/types';
import Button from '~/components/atom/Button';
import Loading from '~/components/organism/SimpleTable/components/Loading';
import CardHeader from '../CardHeader';
import getDateRangeOptions from './utils/getDateRangeOptions';
import BarChart from '~/components/organism/Charts/BarChart';
import { prop, sort, take, descend } from 'ramda';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import { BUILDER_V2_PATH } from '../EmailEffectiveness';
import withMasonryCard from '../withMasonryCard';
import { LINK_PREFIX } from '~/components/page/Automation/v2/components/Wizard';

export type Props = {};

const text = {
  heading: 'Flow effectiviteit',

  noFlowHeader: 'Geen flows',
  noFlowBody: 'Je hebt nog geen flows.',
  noFlowCTA: 'Maak een flow',

  errorHeader: 'Uh-oh 😱',
  errorBody:
    'Het lijkt er op dat er iets mis is gegaan bij het ophalen van jouw contacten. Probeer het later opnieuw.',

  tabsHeader: 'Positieve impact op score',
};

type RowV2 = { id: string; name: string; value: number; link: string };

const FlowEffectiveness: React.FCC<Props> = () => {
  const { id: accountId } = useCurrentAccount();
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);
  const options = getDateRangeOptions();
  const selectedField = options[selectedOptionIndex].payload;
  const variables = {
    accountId,
    limit: 100,
    statisticsLimit: selectedField,
  };

  const {
    data: flows,
    error,
    loading,
  } = useGetFlowsV2StatisticsQuery({
    variables,
  });

  const FilledCardHeader = () => (
    <CardHeader
      headingText={text.heading}
      onOptionChange={({ selectedOptionIdx }) =>
        setSelectedOptionIndex(selectedOptionIdx)
      }
      options={options}
      selectedOptionIndex={selectedOptionIndex}
      dataTestId={TEST_ID.CARD_HEADER}
    />
  );

  if (error) {
    return (
      <>
        <FilledCardHeader />
        <Heading3 size="m">{text.errorHeader}</Heading3>
        <Body>{text.errorBody}</Body>
      </>
    );
  }

  const noFlowsExist = flows && flows.getFlowsV2.items.length <= 0;

  if (noFlowsExist) {
    return (
      <>
        <FilledCardHeader />
        <Heading3 size="m">{text.noFlowHeader}</Heading3>
        <Body>{text.noFlowBody}</Body>
        <Button
          size="medium"
          dataTestId={TEST_ID.NO_FLOWS_CTA}
          onClick={() => navigate(`${LINK_PREFIX}/v2/`)}
          label={text.noFlowCTA}
        />
      </>
    );
  }

  const rowsV2 =
    flows?.getFlowsV2.items.map(({ name, id, Statistics }) => ({
      id,
      name,
      value: Statistics?.leadScoreAverage ?? 0,
      link: `${BUILDER_V2_PATH}${id}`,
    })) ?? [];

  return (
    <>
      <FilledCardHeader />
      <Tabs>
        {[
          <TabBody label={text.tabsHeader} key="thing">
            {loading ? (
              <Loading />
            ) : (
              <BarChart
                dataTestId={TEST_ID.CHART}
                data={take(5, sort<RowV2>(byValue, rowsV2))}
                formatOptions={{
                  style: 'decimal',
                  minimumFractionDigits: 0,
                  signDisplay: 'exceptZero',
                }}
                tooltipLabel="Impact score"
              />
            )}
          </TabBody>,
        ]}
      </Tabs>
    </>
  );
};

const byValue = descend<RowV2>(prop('value'));

export default withMasonryCard(FlowEffectiveness, {
  containerTestId: TEST_ID.CONTAINER,
  elevated: true,
});
